import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ICONS from "../../../../assets/Icons";
import CommonToast from "../../../../components/_utils/CommonToast";
import { env } from "../../../../env";
import axiosInstance from "../../../globalInspector/axiosInstance";

const baseUrl = env.REACT_APP_BASE_URL;

const getHeaders = () => ({
  "Content-Type": "application/json",
  "X-Api-Key": sessionStorage.getItem("q2p_token"),
});

export const getAllInternalAccounts = createAsyncThunk(
  "internalAccount/getAllInternalAccounts",
  async (values, { rejectWithValue }) => {
    const {
      page_no,
      page_size,
      approval_status,
      categoryName,
      search_string,
      bank_account_no,
      ifsc,
      start_date,
      end_date,
      pan,
      labels,
    } = values;

    try {
      const response = await axiosInstance.get(`${baseUrl}/accounts/internal`, {
        headers: getHeaders(),
        params: {
          page_no,
          page_size,
          approval_status: Array.isArray(approval_status)
            ? approval_status
            : approval_status
              ? [approval_status]
              : "",
          category: categoryName,
          search_string,
          bank_account_no,
          ifsc,
          start_date,
          end_date,
          pan,
          labels,
        },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const addInternalAccountLabel = createAsyncThunk(
  "internalAccount/addInternalAccountLabel",
  async ({ record_ids, labels }, { rejectWithValue }) => {
    const data = {
      record_ids: record_ids,
      labels: labels,
    };

    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/internal/add_label`,
        data,
        {
          headers: getHeaders(),
        },
      );

      return response?.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const removeInternalAccountLabel = createAsyncThunk(
  "internalAccount/removeInternalAccountLabel",
  async ({ record_ids, labels }, { rejectWithValue }) => {
    const data = {
      record_ids: record_ids,
      labels: labels,
    };
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/internal/remove_label`,
        data,
        {
          headers: getHeaders(),
        },
      );

      return response?.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const internalAccountDetailsById = createAsyncThunk(
  "internalAccount/internalAccountDetailsById",
  async (values) => {
    const { id } = values ? values : { id: null };

    try {
      const response = await axiosInstance.get(
        `${baseUrl}/accounts/internal/${id}`,
        {
          headers: getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  },
);

export const updateInternalAccount = createAsyncThunk(
  "internalAccount/updateInternalAccount",
  async (values, { rejectWithValue }) => {
    const {
      id,
      recipient_email,
      email,
      contact_no,
      name,
      type_of_account,
      navigate,
    } = values;

    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/internal/${id}/edit_other_details`,
        {
          internal_accounts: {
            recipient_email,
            email,
            contact_no,
            name,
            type_of_account,
            navigate,
          },
        },
        {
          headers: getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      let errorMessage;

      if (error.response) {
        if (error.response.data?.errors?.user_unauthorized) {
          navigate("/feature_not_assigned", {
            state: { unavailableService: "IUAccount" },
          });
        }
        errorMessage = error.response.data?.errors?.recipient_email?.[0];
      } else {
        errorMessage = error.message;
      }
      CommonToast({
        text: errorMessage,
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      return rejectWithValue(errorMessage);
    }
  },
);

export const approveInternalAccount = createAsyncThunk(
  "internalAccount/approveInternalAccount",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/internal/${id}/approve`,
        {},
        {
          validateStatus: () => true,
        },
      );

      if (!response.data?.status) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const rejectInternalAccount = createAsyncThunk(
  "internalAccount/rejectInternalAccount",
  async ({ id, rejection_reason }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/internal/${id}/reject`,
        {
          rejection_reason,
        },
        {
          headers: getHeaders(),
        },
      );

      if (!response.data?.status) {
        return rejectWithValue(response.data);
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

const internalAccountSlice = createSlice({
  name: "internalAccount",
  initialState: {
    storedPage: 1,
    toggle: false,
    internalAccountAvailableLabels: [],

    getAllInternalAccountData: null,
    getAllInternalAccountLoading: false,
    getAllInternalAccountError: null,

    addLabelsInternalAccountData: null,
    addLabelsInternalAccountLoading: false,
    addLabelsInternalAccountError: null,

    removeLabelsInternalAccountData: null,
    removeLabelsInternalAccountLoading: false,
    removeLabelsInternalAccountError: null,

    internalAccountDetailsData: null,
    internalAccountDetailsLoading: false,
    internalAccountDetailsError: false,

    updateInternalAccountData: null,
    updateInternalAccountLoading: false,
    updateInternalAccountError: false,

    approveInternalAccountData: null,
    approveInternalAccountLoading: false,
    approveInternalAccountError: false,

    rejectInternalAccountData: null,
    rejectInternalAccountLoading: false,
    rejectInternalAccountError: false,
  },
  reducers: {
    setInternalToggle: (state, action) => {
      state.toggle = action.payload;
    },
    resetInternalToggle: (state) => {
      state.toggle = false;
    },
    setAvailabelLabels: (state, action) => {
      state.internalAccountAvailableLabels = action.payload;
    },
    resetEditInternalAccount: (state, action) => {
      state.updateInternalAccountData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllInternalAccounts.fulfilled, (state, action) => {
        state.getAllInternalAccountLoading = false;
        state.getAllInternalAccountData = action.payload;
        state.internalAccountAvailableLabels =
          action?.payload?.summary?.available_labels;
        if (action?.payload?.summary?.available_labels) {
          sessionStorage.setItem(
            "internalAccountLabels",
            JSON.stringify(action?.payload?.summary?.available_labels || []),
          );
        }
      })
      .addCase(getAllInternalAccounts.rejected, (state, action) => {
        state.getAllInternalAccountError = true;
        state.getAllInternalAccountData = null;
        state.getAllInternalAccountLoading = false;
      })
      .addCase(getAllInternalAccounts.pending, (state, action) => {
        state.getAllInternalAccountLoading = true;
        state.internalAccountAvailableLabels = [];
      })
      .addCase(addInternalAccountLabel.fulfilled, (state, action) => {
        state.addLabelsInternalAccountLoading = false;
        state.addLabelsInternalAccountData = action.payload;
      })
      .addCase(addInternalAccountLabel.rejected, (state, action) => {
        state.addLabelsInternalAccountError = true;
        state.addLabelsInternalAccountData = null;
        state.addLabelsInternalAccountLoading = false;
      })
      .addCase(addInternalAccountLabel.pending, (state, action) => {
        state.addLabelsInternalAccountLoading = true;
      })
      .addCase(removeInternalAccountLabel.fulfilled, (state, action) => {
        state.removeLabelsInternalAccountLoading = false;
        state.removeLabelsInternalAccountData = action.payload;
      })
      .addCase(removeInternalAccountLabel.rejected, (state, action) => {
        state.removeLabelsInternalAccountError = true;
        state.removeLabelsInternalAccountData = null;
        state.removeLabelsInternalAccountLoading = false;
      })
      .addCase(removeInternalAccountLabel.pending, (state, action) => {
        state.removeLabelsInternalAccountLoading = true;
      })
      .addCase(internalAccountDetailsById.pending, (state) => {
        state.internalAccountDetailsLoading = true;
      })
      .addCase(internalAccountDetailsById.fulfilled, (state, action) => {
        state.internalAccountDetailsLoading = false;
        state.internalAccountDetailsData = action.payload;
        state.internalAccountDetailsError = false;
      })
      .addCase(internalAccountDetailsById.rejected, (state, action) => {
        state.internalAccountDetailsLoading = false;
        state.internalAccountDetailsError = action.payload;
      })
      .addCase(updateInternalAccount.pending, (state) => {
        state.updateInternalAccountLoading = true;
      })
      .addCase(updateInternalAccount.fulfilled, (state, action) => {
        state.updateInternalAccountLoading = false;
        state.updateInternalAccountData = action.payload;
      })
      .addCase(updateInternalAccount.rejected, (state) => {
        state.updateInternalAccountLoading = false;
        state.updateInternalAccountError = true;
      })
      .addCase(approveInternalAccount.pending, (state) => {
        state.approveInternalAccountLoading = true;
      })
      .addCase(approveInternalAccount.fulfilled, (state, action) => {
        state.approveInternalAccountLoading = false;
        state.approveInternalAccountData = action.payload;
        state.approveInternalAccountError = false;
      })
      .addCase(approveInternalAccount.rejected, (state) => {
        state.approveInternalAccountLoading = false;
        state.approveInternalAccountError = true;
      })
      .addCase(rejectInternalAccount.pending, (state) => {
        state.rejectInternalAccountLoading = true;
      })
      .addCase(rejectInternalAccount.fulfilled, (state, action) => {
        state.rejectInternalAccountLoading = false;
        state.rejectInternalAccountData = action.payload;
        state.rejectInternalAccountError = false;
      })
      .addCase(rejectInternalAccount.rejected, (state) => {
        state.rejectInternalAccountLoading = false;
        state.rejectInternalAccountError = true;
      });
  },
});
export const {
  setInternalToggle,
  resetInternalToggle,
  resetEditInternalAccount,
} = internalAccountSlice.actions;

export default internalAccountSlice.reducer;
